import type {
  EditorialLookGridItem,
  EditorialLookGridProps,
} from '@design-system/components/Editorial/EditorialLookGrid.props'
import type { Image, Video } from '@design-system/components/UI/UIMedia.props'

/**
 * Custom composable function for managing an editorial look grid component
 *
 * @param props The props containing items and configuration for the look grid
 * @returns Object containing reactive properties and functions for managing the look grid
 */
export const useEditorialLookGrid = (props: EditorialLookGridProps) => {
  // Currently visible items
  const visibleItems = ref<EditorialLookGridItem[]>([])

  // Total number of items available
  const itemsCount = props.items.length

  /**
   * Loads more items into the visibleItems array based on the itemsPerPage configuration
   *
   * @param loadAllItems If true, loads all remaining items; otherwise, loads up to itemsPerPage
   */
  const loadMoreItems = (loadAllItems = false) => {
    const start = visibleItems.value.length
    const end = loadAllItems
      ? itemsCount
      : Math.min(start + props.itemsPerPage, itemsCount)
    visibleItems.value.push(...props.items.slice(start, end))
  }

  /**
   * Creates a media object (Image or Video) from an EditorialLookGridItem
   *
   * @param item The item containing media information
   * @returns Image or Video object based on the type of media
   */
  const createUIMediaObject = (item: EditorialLookGridItem) => {
    if (item.type === 'image') {
      return {
        type: 'image',
        srcImage: item.media,
        alt: item.media.public_id,
      } as Image
    } else {
      return {
        type: 'video',
        srcVideo: item.media,
        autoplay: false,
        muted: true,
        showCommands: true,
        commandsColor: 'white',
        commandsPosition: 'bottom-right',
      } as Video
    }
  }

  // Initially load itemsPerPage items
  loadMoreItems()

  return {
    visibleItems,
    itemsCount,
    loadMoreItems,
    createUIMediaObject,
  }
}
